import { Component, Input, OnInit } from '@angular/core';
import { ConfirmationService, MessageService, SelectItem } from 'primeng';
import { JobStatus } from '../../../../enums/job-status';
import { NgxGalleryImage } from '../../../../user-controls/ngx-gallery/ngx-gallery-image.model';
import { NgxGalleryOptions } from '../../../../user-controls/ngx-gallery/ngx-gallery-options.model';
import { AdditionalChargeStatusEnum } from '../../../../enums/additional-charge.enum';
import {
  AdditionalChargeService,
  FileStorageService,
  IncidentsService,
} from '../../../../services';
import { InputHelper } from '../../../../utils/input.helper';
import {
  SupplierAdditionalCharge,
  SupplierAdditionalChargeJob,
} from '../../../../interfaces/screen-specific/job-screen/supplier/supplier-job-screen-additional-charge';
import { Message } from '../../../../classes/Message';
import moment from 'moment';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';

@Component({
  selector: 'supplier-job-screen-additional-costs',
  templateUrl: './supplier-job-screen-additional-costs.component.html',
  styleUrls: [
    './supplier-job-screen-additional-costs.component.scss',
    '../../common/job-screen-common.scss',
    '../../common/common-scss/job-screen-additional-costs-common.scss',
  ],
})
export class SupplierJobScreenAdditionalCostsComponent implements OnInit {
  @Input() jobId: number;

  public editingAdditionalCharge: boolean;
  public showUpsertDialog: boolean;
  public additionalChargeToUpsert: any = {};
  public chargeTypeOptions: SelectItem[] = [];
  public taskOptions: SelectItem[] = [];
  public selectedAdditionalCharge: SupplierAdditionalCharge;

  public canAddExpense: boolean = false;

  public job: SupplierAdditionalChargeJob;
  public additionalCosts: SupplierAdditionalCharge[] = [];
  public files: any;
  public expenseImages: NgxGalleryImage[] = [];
  public expenseGalleryOptions: NgxGalleryOptions[] = [
    {
      lazyLoading: false,
      image: false,
      startIndex: 0,
      showPreview: true,
      previewZoom: true,
      preview: true,
      zoomInIcon: 'fa fa-plus',
      zoomOutIcon: 'fa fa-minus',
      rotateLeftIcon: 'fa fa-rotate-left',
      rotateRightIcon: 'fa fa-rotate-right',
      rotate: true,
      thumbnails: false,
    },
  ];

  public get deleteDisabled(): boolean {
    return (
      !this.selectedAdditionalCharge ||
      !this.taskOptions.filter((x) => x.value == this.selectedAdditionalCharge.taskId).length ||
      this.selectedAdditionalCharge.additionalChargeStatusId == AdditionalChargeStatusEnum.Approved
    );
  }

  public get editDisabled(): boolean {
    return !this.selectedAdditionalCharge || !this.selectedAdditionalCharge.canEdit;
  }
  getStatusName(statusName: string) {
    if (statusName === 'Internal Request' || statusName === 'Internal Updated') return 'Pending';
    return statusName;
  }

  additionalChargeValid(): boolean {
    return (
      !isNaN(this.additionalChargeToUpsert.chargeAmount) &&
      this.additionalChargeToUpsert.chargeAmount > 0 &&
      this.additionalChargeToUpsert.chargeAmount <= 9999999.99
    );
  }

  saveAdditionalChargeDisabled(): boolean {
    return (
      !this.additionalChargeValid() || !this.additionalChargeToUpsert.supplierCanUpdateExpenses
    );
  }

  constructor(
    private additionalChargeService: AdditionalChargeService,
    private incidentService: IncidentsService,
    private confirmationService: ConfirmationService,
    public inputHelper: InputHelper,
    private messageService: MessageService,
    private fileStorageService: FileStorageService,
  ) {}

  ngOnInit(): void {
    void this.loadAdditionalCharges();
  }

  async loadAdditionalCharges() {
    if (!(this.jobId && this.jobId != 0)) {
      return;
    }

    try {
      this.selectedAdditionalCharge = null;

      const result = await this.additionalChargeService.getSupplierAdditionalCostsForJob(
        this.jobId,
      );

      this.additionalCosts = result.additionalCharges;
      this.job = result.job;
      this.canAddExpense = result.canAddExpense;

      this.chargeTypeOptions = result.chargeTypes.map((x) => ({
        label: x.chargeType,
        value: x.id,
      }));

      this.taskOptions = result.job.taskList
        .filter((x) => x.supplierCanUpdateExpenses)
        .map((x) => ({
          label: x.vehicle.registrationNumber,
          value: x.id,
        }));
    } catch (e) {
      // Handled by additionalChargeService
    }
  }

  openPhoto(publicUrl: string, photoPath: string) {
    const extensions = photoPath.split('.');
    const extension = extensions[extensions.length - 1] === 'pdf' ? 'pdf' : 'jpg';
    this.expenseImages.push({
      url: publicUrl,
      extension,
      big: publicUrl,
      medium: publicUrl,
      small: publicUrl,
      key: photoPath,
      leftIcon: null,
    });
  }

  editAdditionalCharge(id: number): void {
    this.editingAdditionalCharge = true;

    this.additionalChargeService.get(id).then((result) => {
      result.createdOn = new Date(result.createdOn);
      this.additionalChargeToUpsert = result;

      this.showUpsertDialog = true;
    });
  }

  deleteAdditionalCharge(item: SupplierAdditionalCharge): void {
    this.confirmationService.confirm({
      header: 'Confirmation',
      message: `Are you sure want to delete this charge ?`,
      key: 'delete-charge',
      accept: () => {
        this.additionalChargeService.deleteAdditionalCharge(item).then(() => {
          void this.loadAdditionalCharges();
          this.incidentService.refreshStatistics();
        });
      },
      reject: () => {},
    });
  }

  onExpenseImagePreviewClose(): void {
    this.expenseImages = [];
  }

  getApprovalStatusClass(item: SupplierAdditionalCharge): string {
    return item.additionalChargeStatus?.name?.toLowerCase().replace(/\s+/g, '');
  }

  addAdditionalCharge(): void {
    this.additionalChargeToUpsert = {
      additionalChargeTypeId: this.chargeTypeOptions[0].value,
      taskId: this.taskOptions[0].value,
      supplierCanUpdateExpenses: true,
    };
    this.editingAdditionalCharge = false;
    this.showUpsertDialog = true;
  }

  isAddAdditionalCostReadOnly(): boolean {
    if (this.job == null) {
      return false;
    }

    return !this.additionalChargeToUpsert.supplierCanUpdateExpenses;
  }

  onBeforeUpload(event: any): void {
    let lmDate = this.files[0].LastModifiedDate;
    if (!lmDate) lmDate = new Date();

    event.formData.append('lastModifiedDate', lmDate.getTime());
    event.formData.append('size', '400');
    event.formData.append('jobId', this.jobId);
  }

  onError(event: any): void {
    let errorDescription = 'The following files were not be uploaded: <br/>';
    if (event.files.length > 0) {
      for (let file of event.files) {
        errorDescription += file.name + '<br/>';
      }
    }

    this.messageService.add(
      Message.Error(
        'An error occurred while uploading files.Please try again.<br />' + errorDescription,
        'File Uploading',
      ),
    );
  }

  onAdditionalCostFileUpload(event: any): void {
    let response = event.originalEvent.body;

    if (response.successfully) {
      this.additionalChargeToUpsert.photographLocation = response.fileData.guid;
    } else {
      this.messageService.add(Message.Error(response.errorMessage, 'File Uploading'));
    }
  }

  onSelect(event: any): void {
    this.files = [];
    if (event.files.length > 0) {
      for (let file of event.files) {
        const fileDetails = {
          LastModifiedDate: file.lastModifiedDate,
          Name: file.name,
          Size: file.size,
          Type: file.type,
        };
        this.files.push(fileDetails);
      }
    }
  }

  closeAdditionalChargeUpsertDialog(): void {
    this.additionalChargeToUpsert = {};
    this.showUpsertDialog = false;
  }

  openPhotoPresigned(photoUrl: string): void {
    this.fileStorageService.getPreSignedURLs([photoUrl]).then((url) => {
      const extensions = photoUrl.split('.');
      const extension = extensions[extensions.length - 1] === 'pdf' ? 'pdf' : 'jpg';
      this.expenseImages.push({
        url: url[0],
        extension,
        big: url[0],
        medium: url[0],
        small: url[0],
        key: photoUrl,
        leftIcon: null,
      });
    });
  }

  async saveAdditionalCharge(): Promise<void> {
    const item = this.additionalChargeToUpsert;

    item.chargeAmount = Number(item.chargeAmount);

    let saveItem = { ...item };

    saveItem.createdOn = saveItem.createdOn || moment().format('YYYY-MM-DD HH:mm');

    saveItem.additionalChargeType = null;
    saveItem.additionalChargeStatus = null;
    saveItem.task = null;

    try {
      await this.additionalChargeService.save(saveItem);
      this.showUpsertDialog = false;
      void this.loadAdditionalCharges();
    } catch (e) {
      // Handled by additionalChargeService
    }
  }

  asAdditionalCost(charge: any): SupplierAdditionalCharge {
    return charge as SupplierAdditionalCharge;
  }
}
